import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Flame, HeartPulse, AlertTriangle, Activity, ArrowRight, UtensilsCrossed, Bell, Droplets, Moon, Dumbbell as DumbbellIcon, Apple, Wind, Footprints, Sparkles, Move, Shield, Zap } from "lucide-react";

const EX_CATEGORY = {
  warmup: "warmup", stretch: "stretch", gentle_walk: "walk", outdoor_walk: "walk",
  box_breathing: "breath", cat_cow: "yoga", child_pose: "yoga", seated_twist: "yoga",
  wall_pushup: "strength", chair_squat: "strength", sun_salute_slow: "yoga",
  hip_opener: "stretch", shoulder_roll: "mobility", foam_roll: "mobility", hydration: "hydration",
  squat: "strength", pushup: "strength", rows: "strength", plank: "core",
  jog_steady: "cardio", cooldown: "stretch", hollow_hold: "core", bird_dog: "core",
  side_plank: "core", pike_pushup: "strength", pullup: "strength", db_press: "strength",
  face_pull: "strength", hiit_intervals: "hiit", long_walk_bike: "cardio",
  yoga_flow_light: "yoga", rest_full: "rest",
};
const CAT_STYLE_D = {
  warmup:    { Icon: Flame,      bg: "bg-orange-100", color: "text-orange-700" },
  stretch:   { Icon: Wind,       bg: "bg-yellow-100", color: "text-yellow-700" },
  walk:      { Icon: Footprints, bg: "bg-green-100",  color: "text-green-700" },
  breath:    { Icon: Wind,       bg: "bg-sky-100",    color: "text-sky-700" },
  yoga:      { Icon: Sparkles,   bg: "bg-purple-100", color: "text-purple-700" },
  strength:  { Icon: DumbbellIcon, bg: "bg-[#EAF0E4]", color: "text-[#4A5D23]" },
  mobility:  { Icon: Move,       bg: "bg-cyan-100",   color: "text-cyan-700" },
  hydration: { Icon: Droplets,   bg: "bg-blue-100",   color: "text-blue-700" },
  core:      { Icon: Shield,     bg: "bg-indigo-100", color: "text-indigo-700" },
  cardio:    { Icon: Activity,   bg: "bg-rose-100",   color: "text-rose-700" },
  hiit:      { Icon: Zap,        bg: "bg-red-100",    color: "text-red-700" },
  rest:      { Icon: Moon,       bg: "bg-slate-100",  color: "text-slate-700" },
};

function ExerciseIconThumb({ item }) {
  const cat = (item && typeof item === "object") ? EX_CATEGORY[item.key] : null;
  const style = CAT_STYLE_D[cat] || CAT_STYLE_D.strength;
  const Icon = style.Icon;
  return (
    <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${style.bg} ${style.color}`}>
      <Icon className="w-4 h-4" />
    </span>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const { t, lang } = useLang();
  const [summary, setSummary] = useState(null);
  const [menu, setMenu] = useState(null);
  const [menuLoading, setMenuLoading] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    const [s, r] = await Promise.all([
      api.get("/dashboard/summary"),
      api.get("/reminders/today", { params: { lang } }).catch(() => ({ data: { reminders: [] } })),
    ]);
    setSummary(s.data);
    setReminders(r.data.reminders || []);
  };

  const loadMenu = async (refresh = false) => {
    setMenuLoading(true);
    try {
      const { data } = await api.get("/menu/today", { params: { lang, ...(refresh ? { refresh: 1 } : {}) } });
      setMenu(data.menu);
    } finally {
      setMenuLoading(false);
    }
  };

  useEffect(() => {
    load();
    loadMenu();
    // browser notifications
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang]);

  useEffect(() => {
    if (user && !user.has_profile) nav("/onboarding");
  }, [user, nav]);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post("/workouts/generate");
      await load();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="dashboard">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#4A5D23] font-bold">Gracely</p>
        <h1 className="font-serif-display text-4xl text-stone-900 mt-2">{t("dash_welcome")}, {user?.name?.split(" ")[0]}.</h1>
        <p className="text-stone-500 mt-1">
          {lang === "id" ? "Semoga harimu tenang. Ini gambaran singkatnya." : "Hope your day is calm. Here's a quick overview."}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card-soft p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center"><Flame className="w-5 h-5" /></div>
            <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{t("dash_streak")}</p>
          </div>
          <p className="mt-6 font-serif-display text-5xl text-stone-900" data-testid="streak-value">{summary?.streak ?? 0}<span className="text-lg text-stone-400 ml-2">{lang === "id" ? "hari" : "days"}</span></p>
        </div>
        <div className="card-soft p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FFEDE7] text-[#E07A5F] flex items-center justify-center"><AlertTriangle className="w-5 h-5" /></div>
            <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{t("dash_alerts")}</p>
          </div>
          <p className="mt-6 font-serif-display text-5xl text-stone-900" data-testid="alerts-value">{summary?.nutrition_alerts ?? 0}</p>
        </div>
        <div className="card-soft p-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center"><HeartPulse className="w-5 h-5" /></div>
            <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Kondisi" : "Conditions"}</p>
          </div>
          <p className="mt-6 text-stone-800 text-lg" data-testid="conditions-value">
            {user?.has_profile ? (lang === "id" ? "Profil aktif" : "Profile active") : (lang === "id" ? "Belum lengkap" : "Incomplete")}
          </p>
        </div>
      </div>

      {/* Reminders */}
      {reminders.length > 0 && (
        <div className="card-soft p-6" data-testid="reminders-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded-xl bg-[#FDF3D9] text-[#8A6B00] flex items-center justify-center"><Bell className="w-4 h-4" /></div>
            <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Pengingat lembut hari ini" : "Gentle reminders for today"}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {reminders.map((r) => (
              <ReminderPill key={r.id} r={r} />
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Today's workout with image */}
        <div className="card-soft overflow-hidden md:col-span-2" data-testid="today-workout-card">
          {summary?.today_workout ? (
            <>
              {summary.today_workout.image && (
                <div className="relative h-52 overflow-hidden">
                  <img src={summary.today_workout.image} alt="" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  <div className="absolute inset-x-6 bottom-4 text-white">
                    <p className="text-[10px] uppercase tracking-widest opacity-80 font-bold">{t("dash_today")}</p>
                    <p className="font-serif-display text-3xl leading-tight mt-1">{lang === "id" ? summary.today_workout.title : summary.today_workout.title_en}</p>
                  </div>
                </div>
              )}
              <div className="p-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-stone-500 flex items-center gap-2">
                    <Activity className="w-3 h-3" /> {summary.today_workout.duration_min} min · {summary.today_workout.tag}
                  </p>
                  <Link to="/workouts" className="text-xs text-[#4A5D23] font-semibold inline-flex items-center gap-1">
                    {lang === "id" ? "Lihat semua" : "See all"} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
                <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {summary.today_workout.items.map((it, i) => {
                    const label = (it && typeof it === "object") ? (lang === "id" ? it.name : it.name_en) || it.name : it;
                    const meta = (it && typeof it === "object") ? it.meta : "";
                    return (
                      <li key={i} className="text-sm text-stone-700 bg-stone-50 px-4 py-2 rounded-lg flex items-center gap-3">
                        <ExerciseIconThumb item={it} />
                        <div className="min-w-0 flex-1">
                          <p className="truncate">{label}</p>
                          {meta && <p className="text-xs text-stone-500 truncate">{meta}</p>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </>
          ) : (
            <div className="p-8">
              <p className="text-stone-600">{t("dash_no_workout")}</p>
              <Button onClick={generate} disabled={loading} className="mt-4 rounded-full bg-[#E07A5F] hover:bg-[#C96D55] text-white btn-pop" data-testid="generate-plan-btn">
                {loading ? "…" : t("generate_plan")}
              </Button>
            </div>
          )}
        </div>

        {/* Recent scans */}
        <div className="card-soft p-6">
          <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Pindaian Terakhir" : "Recent Scans"}</p>
          <div className="mt-4 space-y-3">
            {(summary?.recent_scans || []).length === 0 && (
              <p className="text-sm text-stone-500">{lang === "id" ? "Belum ada pindaian." : "No scans yet."}</p>
            )}
            {(summary?.recent_scans || []).map((s) => (
              <Link key={s.id} to="/scan" className="block p-3 rounded-xl border border-stone-200 hover:border-[#4A5D23]/40">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold text-stone-800 capitalize">{s.source}</p>
                  <VerdictPill verdict={s.analysis?.verdict} lang={lang} t={t} />
                </div>
                <p className="mt-1 text-xs text-stone-500 truncate">{s.raw_text}</p>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Daily Menu */}
      <div className="card-soft p-6" data-testid="menu-card">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#EAF0E4] text-[#4A5D23] flex items-center justify-center"><UtensilsCrossed className="w-5 h-5" /></div>
            <div>
              <p className="text-xs uppercase tracking-widest text-stone-500 font-bold">{lang === "id" ? "Menu aman hari ini" : "Today's safe menu"}</p>
              <p className="font-serif-display text-2xl text-stone-900">{lang === "id" ? "Dari Gracely, sesuai profilmu" : "From Gracely, tailored to you"}</p>
            </div>
          </div>
          <Button onClick={() => loadMenu(true)} disabled={menuLoading} variant="ghost" className="text-[#4A5D23]" data-testid="refresh-menu-btn">
            {menuLoading ? "…" : (lang === "id" ? "Segarkan" : "Refresh")}
          </Button>
        </div>
        {!menu && !menuLoading && <p className="mt-4 text-sm text-stone-500">{lang === "id" ? "Menu sedang disiapkan…" : "Preparing menu…"}</p>}
        {menu && (
          <div className="mt-5 space-y-4">
            {menu.summary && <p className="text-sm text-stone-700 leading-relaxed">{menu.summary}</p>}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <MealCard icon="🌅" label={lang === "id" ? "Sarapan" : "Breakfast"} meal={menu.breakfast} testid="meal-breakfast" />
              <MealCard icon="🍚" label={lang === "id" ? "Makan Siang" : "Lunch"} meal={menu.lunch} testid="meal-lunch" />
              <MealCard icon="🍎" label={lang === "id" ? "Camilan" : "Snack"} meal={menu.snack} testid="meal-snack" />
              <MealCard icon="🌙" label={lang === "id" ? "Makan Malam" : "Dinner"} meal={menu.dinner} testid="meal-dinner" />
            </div>
            {menu.avoid && menu.avoid.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-widest text-stone-500 font-bold mb-2">{lang === "id" ? "Hindari hari ini" : "Avoid today"}</p>
                <div className="flex flex-wrap gap-2">
                  {menu.avoid.map((a, i) => (
                    <span key={i} className="text-xs px-3 py-1 rounded-full bg-[#FFEDE7] text-[#8A3A25] font-semibold" data-testid={`avoid-${i}`}>{a}</span>
                  ))}
                </div>
              </div>
            )}
            {menu.source && (
              <div className="flex flex-wrap gap-1.5">
                {String(menu.source).split(/[,;/]/).map((s, i) => (
                  <span key={i} className="text-[10px] font-bold uppercase tracking-wide bg-[#EAF0E4] text-[#4A5D23] px-2 py-0.5 rounded-full">
                    {s.trim()}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function MealCard({ icon, label, meal, testid }) {
  if (!meal || !meal.name) return null;
  return (
    <div className="p-4 rounded-xl bg-stone-50 border border-stone-200" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-lg">{icon}</span>
        {meal.kcal && <span className="text-[10px] font-bold uppercase tracking-wide bg-white text-stone-500 px-1.5 py-0.5 rounded-full border border-stone-200">{meal.kcal} kcal</span>}
      </div>
      <p className="text-[10px] uppercase tracking-widest text-stone-500 font-bold mt-3">{label}</p>
      <p className="font-serif-display text-lg text-stone-900 leading-tight mt-1">{meal.name}</p>
      {meal.why && <p className="text-xs text-stone-500 mt-2 leading-snug">{meal.why}</p>}
    </div>
  );
}

function ReminderPill({ r }) {
  const iconMap = { water: Droplets, sleep: Moon, workout: DumbbellIcon, meal: Apple };
  const Icon = iconMap[r.kind] || Bell;
  const colorMap = {
    water: "bg-blue-50 text-blue-700",
    sleep: "bg-indigo-50 text-indigo-700",
    workout: "bg-[#EAF0E4] text-[#4A5D23]",
    meal: "bg-[#FFEDE7] text-[#E07A5F]",
  };
  return (
    <div className="p-3 rounded-xl bg-white border border-stone-200 flex items-start gap-3" data-testid={`reminder-${r.id}`}>
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colorMap[r.kind] || "bg-stone-100 text-stone-600"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between">
          <p className="text-sm font-semibold text-stone-900">{r.title}</p>
          <span className="text-[10px] text-stone-500">{r.suggested_time}</span>
        </div>
        <p className="text-xs text-stone-500 mt-0.5 leading-snug">{r.body}</p>
      </div>
    </div>
  );
}

function VerdictPill({ verdict, lang, t }) {
  const map = {
    safe: { bg: "bg-[#EAF0E4]", color: "text-[#4A5D23]", label: t("verdict_safe") },
    caution: { bg: "bg-[#FDF3D9]", color: "text-[#8A6B00]", label: t("verdict_caution") },
    danger: { bg: "bg-[#FFEDE7]", color: "text-[#E07A5F]", label: t("verdict_danger") },
  };
  const v = map[verdict] || map.safe;
  return <span className={`px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide rounded-full ${v.bg} ${v.color}`}>{v.label}</span>;
}
